import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { usePatient } from '@/hooks/usePatients';
import { usePatientPicker, type PatientPickerItem } from '@/hooks/usePatientPicker';
import { useCreateInvoiceDraft } from '@/hooks/useInvoices';
import { useAppointment } from '@/hooks/useAppointments';
import { useClinic } from '@/hooks/useClinicSettings';
import {
  formatPatientTelephoneNumbers,
  primaryTelephoneNumber,
  readTelephoneSubscribers,
} from '@/lib/patientStamkortFields';
import { Avatar } from '@/components/ui/Avatar';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { FormPage, FormSection } from '@/components/ui/FormPage';
import { PageLayout } from '@/components/ui/PageLayout';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { TYPE, UI_MUTED, UI_SECONDARY } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function NewInvoice() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeClinicId } = useActiveClinic();

  const initialPatientId = searchParams.get('patient_id');
  const initialClinicId = searchParams.get('clinic_id');
  const initialAppointmentId = searchParams.get('appointment_id');
  const returnTo = searchParams.get('return_to');

  const { data: initialPatient, isLoading: loadingInitialPatient } = usePatient(initialPatientId);
  const { data: linkedAppointment } = useAppointment(initialAppointmentId);
  const { data: clinic } = useClinic(initialClinicId ?? activeClinicId);

  const [search, setSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientPickerItem | null>(null);
  const [invoiceClinicId, setInvoiceClinicId] = useState<string | null>(initialClinicId);
  const [dueAt, setDueAt] = useState('');
  const [notes, setNotes] = useState('');
  const [patientError, setPatientError] = useState<string | null>(null);

  const pickerClinicId = invoiceClinicId ?? activeClinicId;
  const picker = usePatientPicker(pickerClinicId, search);
  const create = useCreateInvoiceDraft();

  const paymentTermsDays = clinic?.payment_terms_days ?? 14;

  useEffect(() => {
    if (!clinic || dueAt) return;
    const defaultDueDate = new Date();
    defaultDueDate.setDate(defaultDueDate.getDate() + (clinic.payment_terms_days ?? 14));
    setDueAt(defaultDueDate.toISOString().slice(0, 10));
  }, [clinic, dueAt]);

  useEffect(() => {
    if (!initialPatient) return;
    setSelectedPatient({
      id: initialPatient.id,
      first_name: initialPatient.first_name,
      last_name: initialPatient.last_name,
      email: initialPatient.email,
      phone: primaryTelephoneNumber(
        initialPatient.telephone_subscribers,
        initialPatient.phone,
      ),
      telephone_subscribers: readTelephoneSubscribers(initialPatient.telephone_subscribers),
    });
    setInvoiceClinicId(initialClinicId ?? initialPatient.clinic_id);
  }, [initialPatient, initialClinicId]);

  const backTo = returnTo && returnTo.startsWith('/') ? returnTo : '/economy/invoices';
  const handleBack = () => {
    navigate(backTo);
  };

  const backLabel = returnTo?.startsWith('/patients/')
    ? t('invoices.back_to_patient')
    : t('invoices.back_to_list');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const clinicId = invoiceClinicId ?? activeClinicId;
    if (!clinicId) return;
    if (!selectedPatient) {
      setPatientError(t('invoices.patient_required'));
      toast.error(t('invoices.patient_required'));
      return;
    }
    try {
      const inv = await create.mutateAsync({
        clinic_id: clinicId,
        patient_id: selectedPatient.id,
        appointment_id: initialAppointmentId || undefined,
        due_at: dueAt ? new Date(dueAt).toISOString() : undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(t('invoices.toast_created'));
      navigate(`/invoices/${inv.id}`);
    } catch (err) {
      console.error('create invoice draft failed', err);
      toast.error(t('invoices.save_error'));
    }
  }

  const patientName = selectedPatient
    ? `${selectedPatient.first_name} ${selectedPatient.last_name}`.trim()
    : '';
  const patientPhones = selectedPatient
    ? formatPatientTelephoneNumbers(
        selectedPatient.telephone_subscribers,
        selectedPatient.phone,
      )
    : '';

  return (
    <PageLayout>
    <form onSubmit={onSubmit}>
      <FormPage
        breadcrumb={[
          { label: backLabel, to: backTo },
          { label: t('invoices.new_invoice') },
        ]}
        title={
          <>
            {t('invoices.new_invoice')}
            <span className={cn(TYPE.subtitle, 'mt-s2 block font-normal text-ds-muted')}>
              {t('invoices.new_subtitle')}
            </span>
          </>
        }
        onCancel={handleBack}
        primary={
          <Button
            type="submit"
            loading={create.isPending}
            loadingText={t('common.saving')}
            disabled={!selectedPatient || loadingInitialPatient}
            aria-describedby={!selectedPatient ? 'new-invoice-need-patient' : undefined}
          >
            {t('invoices.actions_save_draft')}
          </Button>
        }
      >
        {create.isPending ? (
          <Banner tone="info">{t('invoices.saving_lock')}</Banner>
        ) : null}

        <FormSection title={t('invoices.section_patient')}>
          {loadingInitialPatient && initialPatientId && !selectedPatient ? (
            <div className={cn('flex items-center gap-s2', UI_MUTED)}>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('common.loading')}
            </div>
          ) : selectedPatient ? (
            <div className="flex items-center gap-s3 rounded-pop bg-ds-sunk p-s3">
              <Avatar name={patientName} size={36} />
              <div className="min-w-0 flex-1">
                <div className={cn(TYPE.section, 'text-ds-text')}>{patientName}</div>
                <div className={cn(UI_MUTED, 'truncate')}>
                  {[selectedPatient.email, patientPhones].filter(Boolean).join(' · ')}
                </div>
              </div>
              {!initialAppointmentId ? (
                <Button
                  type="button"
                  variant="tertiary"
                  size="sm"
                  onClick={() => {
                    setSelectedPatient(null);
                    setInvoiceClinicId(activeClinicId ?? null);
                    setPatientError(null);
                  }}
                >
                  {t('common.edit')}
                </Button>
              ) : null}
            </div>
          ) : (
            <div>
              <Input
                id="new-invoice-patient"
                label={t('invoices.patient_picker_label')}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPatientError(null);
                }}
                placeholder={t('invoices.patient_picker_placeholder')}
                leadingIcon={<Search className="h-4 w-4" />}
                required
                error={patientError ?? undefined}
              />
              {!selectedPatient ? (
                <p id="new-invoice-need-patient" className={cn('mt-s1', UI_SECONDARY)}>
                  {t('invoices.patient_required')}
                </p>
              ) : null}
              {picker.isLoading ? (
                <div className={cn('mt-s2 flex items-center gap-s2', UI_SECONDARY)}>
                  <Loader2 className="h-3 w-3 animate-spin" /> {t('common.loading')}
                </div>
              ) : null}
              {picker.data && picker.data.length > 0 ? (
                <div className="mt-s2 max-h-60 overflow-y-auto rounded-pop border border-ds-line bg-ds-card shadow-pop">
                  {picker.data.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setSelectedPatient(p);
                        setInvoiceClinicId(pickerClinicId ?? activeClinicId ?? null);
                        setSearch('');
                        setPatientError(null);
                      }}
                      className={cn(
                        'flex w-full flex-col items-start border-b border-ds-line px-s3 py-s2 text-left last:border-b-0 hover:bg-ds-sunk',
                        TYPE.body,
                      )}
                    >
                      <span className="text-ds-text">
                        {p.first_name} {p.last_name}
                      </span>
                      {formatPatientTelephoneNumbers(p.telephone_subscribers, p.phone) ? (
                        <span className={UI_SECONDARY}>
                          {formatPatientTelephoneNumbers(p.telephone_subscribers, p.phone)}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </FormSection>

        <FormSection title={t('invoices.section_payment')}>
          <Input
            id="new-invoice-due"
            label={t('invoices.field_due_at')}
            type="date"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            required
            hint={t('invoices.due_hint', { days: paymentTermsDays })}
          />
        </FormSection>

        <FormSection title={t('invoices.section_lines')}>
          <p className={UI_MUTED}>
            {linkedAppointment
              ? linkedAppointment.service_name
                ? t('invoices.linked_appointment', {
                    date: new Date(linkedAppointment.starts_at).toLocaleString(
                      i18n.language === 'da' ? 'da-DK' : 'en-GB',
                      { dateStyle: 'medium', timeStyle: 'short' },
                    ),
                    service: linkedAppointment.service_name,
                  })
                : t('invoices.linked_appointment_no_service', {
                    date: new Date(linkedAppointment.starts_at).toLocaleString(
                      i18n.language === 'da' ? 'da-DK' : 'en-GB',
                      { dateStyle: 'medium', timeStyle: 'short' },
                    ),
                  })
              : t('invoices.lines_after_save')}
          </p>
        </FormSection>

        <FormSection title={t('invoices.section_notes')}>
          <Textarea
            id="new-invoice-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder={t('invoices.notes_placeholder')}
          />
        </FormSection>
      </FormPage>
    </form>
    </PageLayout>
  );
}
